import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Button, CircularProgress, Grid } from '@material-ui/core';
import { Input, LinearProgress } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { fbmApi as api } from 'api/fbm';
import { fbmSmallsRetail } from 'api/FBM/fbmSmallsRetail';
import { UserContext } from 'contexts/UserContext';
import { Notifications, useDebouncedValue } from 'shared';
import { ConfirmDialog } from 'shared/components/ConfirmDialog';
import { fbmOrderDeepDiveQueryKeys, useGetOrderDeepDiveQuery } from 'shared/hooks/fbm/useGetOrderDeepDiveQuery';
import { fileDownload } from 'utils/fileDownload';

import { DeepDiveResults } from './deepDiveResults';

const PACK_LIST_REPORT_ENABLD_FIELD = 'smalls.downloadRetailPackListReport';
const PALLET_LABELS_ENABLED_FIELD = 'smalls.downloadRetailPalletLabels';
/**
 *
 * @param {string} fbmOrderNum - The FBM order number to search for
 * @param {Function} [onFbmOrderNumChange] - Callback when search term changes (updates URL query params)
 */
function ViewOrderDeepDive({ fbmOrderNum, onFbmOrderNumChange }) {
    const queryClient = useQueryClient();
    const [notification, setNotification] = useState({ text: '', level: '' });
    const { fbmAccount } = useContext(UserContext);
    const [isIngesting, setIsIngesting] = useState(false);
    const [downloadingPackList, setDownloadingPackList] = useState(false);
    const [downloadingPalletLabels, setDownloadingPalletLabels] = useState(false);
    const [retailReportConfirm, setRetailReportConfirm] = useState({
        open: false,
        alertMessage: '',
        response: null,
        filename: '',
    });
    const [searchTerm, setSearchTerm] = useState(fbmOrderNum);
    const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);

    const {
        data: deepDiveResults,
        error: errorDeepDiveResults,
        isFetching: isFetchingDeepDiveResults,
        isLoading: isLoadingDeepDiveResults,
    } = useGetOrderDeepDiveQuery(debouncedSearchTerm, fbmAccount.fbmAccountName);

    const isPackListReportEnabled = deepDiveResults?.ext?.[PACK_LIST_REPORT_ENABLD_FIELD];
    const isPalletLabelsEnabled = deepDiveResults?.ext?.[PALLET_LABELS_ENABLED_FIELD];

    const queryErrorMessage = errorDeepDiveResults?.message;
    const notificationOptions = useMemo(() => {
        if (notification.text) {
            return notification;
        }
        if (queryErrorMessage) {
            return { text: queryErrorMessage, level: 'error' };
        }
        return notification;
    }, [queryErrorMessage, notification]);

    // Sync searchTerm when fbmOrderNum prop changes (e.g., URL navigation)
    useEffect(() => {
        setSearchTerm(fbmOrderNum);
    }, [fbmOrderNum]);

    // Update URL query params when debounced search term changes
    useEffect(() => {
        onFbmOrderNumChange?.(debouncedSearchTerm || undefined);
    }, [debouncedSearchTerm, onFbmOrderNumChange]);

    const downloadRetailPackListReport = async () => {
        setDownloadingPackList(true);
        try {
            const partnerOrderNumber = deepDiveResults?.orderNum;
            if (!partnerOrderNumber) {
                setNotification({ text: 'Partner order number not available', level: 'error' });
                return;
            }
            const response = await fbmSmallsRetail.downloadPackListReport(
                partnerOrderNumber,
                fbmAccount.fbmAccountName,
            );

            if (response.ok) {
                const alertHeader = response.headers.get('X-Smalls-Retail-Report-Alert');
                const filename = `pack-list-report-${partnerOrderNumber}.pdf`;
                if (alertHeader) {
                    setRetailReportConfirm({
                        open: true,
                        alertMessage: alertHeader,
                        response,
                        filename,
                    });
                    return;
                }

                await fileDownload(response, filename);
            } else {
                setNotification({ text: 'Error downloading pack list report', level: 'error' });
            }
        } catch {
            setNotification({ text: 'Error downloading pack list report', level: 'error' });
        } finally {
            setDownloadingPackList(false);
        }
    };

    const downloadRetailPalletLabels = async () => {
        setDownloadingPalletLabels(true);
        try {
            const partnerOrderNumber = deepDiveResults?.orderNum;
            if (!partnerOrderNumber) {
                setNotification({ text: 'Partner order number not available', level: 'error' });
                return;
            }
            const response = await fbmSmallsRetail.downloadPalletLabels(partnerOrderNumber, fbmAccount.fbmAccountName);

            if (!response.ok) {
                throw new Error('Error downloading retail labels');
            }

            await fileDownload(response, `retail-labels-${partnerOrderNumber}.pdf`);
        } catch {
            setNotification({ text: 'Error downloading retail labels', level: 'error' });
        } finally {
            setDownloadingPalletLabels(false);
        }
    };

    const handleRetailReportConfirm = async () => {
        const { filename, response } = retailReportConfirm;
        setRetailReportConfirm({ open: false, alertMessage: '', response: null, filename: '' });
        if (response && filename) {
            await fileDownload(response, filename);
        }
    };

    const handleRetailReportCancel = () => {
        setRetailReportConfirm({ open: false, alertMessage: '', response: null, filename: '' });
    };

    const handleIngest = async () => {
        setIsIngesting(true);
        const response = await api.ingestSingleOrder(deepDiveResults.orderNum, fbmAccount.fbmAccountName);
        setIsIngesting(false);
        if (response.status === false) {
            setNotification({
                text: `Unable to ingest ${response.msg}`,
                level: 'error',
            });
            return;
        }
        queryClient.invalidateQueries({
            queryKey: fbmOrderDeepDiveQueryKeys.byOrderNum(deepDiveResults.orderNum),
        });
        setNotification({ text: 'Success', level: 'success' });
    };

    return (
        <div>
            <h1>{fbmAccount.companyName} - Order Deep Dive</h1>
            <Notifications options={notificationOptions} />
            <ConfirmDialog
                open={retailReportConfirm.open}
                title="Download pack list report?"
                description={retailReportConfirm.alertMessage}
                confirmLabel="Download"
                confirmVariant="default"
                onConfirm={handleRetailReportConfirm}
                onCancel={handleRetailReportCancel}
            />
            {!!fbmAccount.id && (
                <div>
                    <Grid container>
                        {Object.keys(deepDiveResults ?? {}).length > 0 && (
                            <Grid item xs={4}>
                                <Button
                                    id="MarkShippedButton"
                                    onClick={async () => {
                                        const response = await api.markOrderShipped(
                                            deepDiveResults.shipmentID,
                                            fbmAccount.fbmAccountName,
                                        );
                                        if (response.status === false) {
                                            setNotification({
                                                text: `Error marking shipped ${response.msg}`,
                                                level: 'error',
                                            });
                                            return;
                                        }

                                        setNotification({ text: 'Success', level: 'success' });
                                    }}
                                >
                                    Mark Order Shipped
                                </Button>
                            </Grid>
                        )}
                        {Object.keys(deepDiveResults ?? {}).length > 0 && (
                            <Grid item xs={4}>
                                <Button
                                    id="IngestButton"
                                    disabled={isIngesting || fbmAccount.fbmAccountName === 'goodhillfarms'}
                                    onClick={handleIngest}
                                >
                                    Ingest
                                </Button>
                            </Grid>
                        )}
                        {isPackListReportEnabled && (
                            <Grid item xs={4}>
                                {downloadingPackList ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    <Button id="DownloadPackListButton" onClick={downloadRetailPackListReport}>
                                        Download Pack List Report
                                    </Button>
                                )}
                            </Grid>
                        )}
                        {isPalletLabelsEnabled && (
                            <Grid item xs={4}>
                                {downloadingPalletLabels ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    <Button id="DownloadRetailLabelsButton" onClick={downloadRetailPalletLabels}>
                                        Download Pallet / Case Labels
                                    </Button>
                                )}
                            </Grid>
                        )}
                        <Grid item xs={12} className="flex flex-col items-center justify-center gap-1 !mt-[40px]">
                            <Input
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                }}
                                className="w-[450px]"
                                placeholder="Enter order number"
                            />
                            <span className="text-xs text-gray-500">Search runs automatically as you type</span>
                        </Grid>
                    </Grid>
                    <br />
                    <br />
                    {(isLoadingDeepDiveResults || isFetchingDeepDiveResults) && <LinearProgress />}
                    {Object.keys(deepDiveResults ?? {}).length > 0 && (
                        <DeepDiveResults companyName={fbmAccount.companyName} deepDiveResults={deepDiveResults} />
                    )}
                </div>
            )}
        </div>
    );
}

export { ViewOrderDeepDive };
